import { getMe, listAccounts, patchMe, setDefaultFundingAccount } from '../api-client';
import type { AccountListItem } from '../api-client';
import { renderAccountPicker } from './account-picker';
import { navigate } from '../router';

export async function renderSettings(root: HTMLElement): Promise<void> {
  root.innerHTML = `
    <header class="page-header">
      <a href="/" class="back-link" id="back">← Home</a>
      <h1>Settings</h1>
    </header>
    <main class="settings">
      <p class="signed-in-as muted" id="signed-in-as">…</p>

      <section class="settings-group">
        <h2>LOGGING</h2>
        <button class="settings-row" id="default-card-row">
          <span>Default card</span>
          <span id="default-card-value" class="muted">…</span>
        </button>
        <button class="settings-row" id="default-funding-row">
          <span>Default funding account</span>
          <span id="default-funding-value" class="muted">…</span>
        </button>
      </section>

      <section class="settings-group">
        <h2>EXPORT</h2>
        <a class="settings-row" href="/api/export/csv" download>
          <span>Download CSV</span><span class="chev">⬇</span>
        </a>
        <a class="settings-row" href="/api/export/json" download>
          <span>Download JSON</span><span class="chev">⬇</span>
        </a>
      </section>

      <section class="settings-group">
        <h2>DATA</h2>
        <div class="settings-row settings-info">
          <span>Last automated backup</span>
          <span id="last-backup" class="muted">…</span>
        </div>
        <div class="settings-row settings-info">
          <span>Last automated price refresh</span>
          <span id="last-price-refresh" class="muted">…</span>
        </div>
      </section>

      <section class="settings-group">
        <h2>ACCOUNT</h2>
        <button class="settings-row danger" id="sign-out">Sign out</button>
      </section>

      <section class="settings-group">
        <h2>IMPORTS</h2>
        <button class="settings-row" id="goto-statements">
          <span>Statement imports</span><span class="chev">›</span>
        </button>
      </section>

      <div id="picker-mount"></div>
    </main>
  `;

  let accounts: AccountListItem[] = [];
  let currentDefault: string | null = null;
  let currentFundingDefault: string | null = null;

  function renderDefaultCardLabel(): void {
    const el = root.querySelector('#default-card-value');
    if (!el) return;
    if (currentDefault) {
      const match = accounts.find((a) => a.id === currentDefault);
      el.textContent = match?.name ?? currentDefault;
    } else {
      el.textContent = 'Not set — tap to choose';
    }
  }

  function renderDefaultFundingLabel(): void {
    const el = root.querySelector('#default-funding-value');
    if (!el) return;
    if (currentFundingDefault) {
      const match = accounts.find((a) => a.id === currentFundingDefault);
      el.textContent = match?.name ?? currentFundingDefault;
    } else {
      el.textContent = 'Not set — tap to choose';
    }
  }

  async function refresh(): Promise<void> {
    try {
      const [me, accs] = await Promise.all([getMe(), listAccounts()]);
      accounts = accs.accounts;
      currentDefault = me.default_account_id;
      currentFundingDefault = me.default_funding_account_id;

      if (me.user) {
        root.querySelector('#signed-in-as')!.textContent = `Signed in as ${me.user.displayName}`;
      }
      renderDefaultCardLabel();
      renderDefaultFundingLabel();
      const lastEl = root.querySelector('#last-backup')!;
      lastEl.textContent = me.lastAutomatedBackupAt ? formatBackupAge(me.lastAutomatedBackupAt) : 'never';
      const lastPriceEl = root.querySelector('#last-price-refresh')!;
      lastPriceEl.textContent = me.lastAutomatedPriceRefreshAt ? formatBackupAge(me.lastAutomatedPriceRefreshAt) : 'never';
    } catch (err) {
      console.error('[settings] refresh failed', err);
      const valueEl = root.querySelector('#default-card-value');
      if (valueEl) valueEl.textContent = 'Tap to choose';
      const fundingEl = root.querySelector('#default-funding-value');
      if (fundingEl) fundingEl.textContent = 'Tap to choose';
      const backupEl = root.querySelector('#last-backup');
      if (backupEl) backupEl.textContent = '—';
      const priceRefreshEl = root.querySelector('#last-price-refresh');
      if (priceRefreshEl) priceRefreshEl.textContent = '—';
    }
  }

  await refresh();

  root.querySelector<HTMLButtonElement>('#default-card-row')!.onclick = () => {
    const mount = root.querySelector<HTMLElement>('#picker-mount')!;
    renderAccountPicker(mount, accounts, {
      onPick: async (id) => {
        try {
          await patchMe({ default_account_id: id });
          mount.innerHTML = '';
          currentDefault = id;
          renderDefaultCardLabel();
          await refresh();
        } catch (e) {
          mount.innerHTML = `<p class="muted">Couldn't save: ${String(e)}</p>`;
        }
      },
      onCancel: () => { mount.innerHTML = ''; },
    }, { filter: 'liability_credit', selectedId: currentDefault });
  };

  root.querySelector<HTMLButtonElement>('#default-funding-row')!.onclick = () => {
    const mount = root.querySelector<HTMLElement>('#picker-mount')!;
    renderAccountPicker(mount, accounts, {
      onPick: async (id) => {
        mount.innerHTML = '';
        currentFundingDefault = id;
        renderDefaultFundingLabel();
        try {
          await setDefaultFundingAccount(id);
        } catch (e) {
          console.error('[settings] setDefaultFundingAccount failed', e);
        }
      },
      onCancel: () => { mount.innerHTML = ''; },
    }, { filter: 'asset_spendable', selectedId: currentFundingDefault });
  };

  root.querySelector<HTMLButtonElement>('#sign-out')!.onclick = async () => {
    try { await fetch('/api/auth/logout', { method: 'POST' }); }
    finally { window.location.href = '/'; }
  };

  root.querySelector<HTMLButtonElement>('#goto-statements')!.onclick = () => { void navigate('/statements'); };

  root.querySelector<HTMLAnchorElement>('#back')!.onclick = (e) => {
    e.preventDefault();
    void navigate('/');
  };
}

function formatBackupAge(iso: string): string {
  const date = new Date(iso);
  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  const ymd = date.toISOString().slice(0, 10);
  return days <= 0 ? `${ymd} (today)` : `${ymd} (${days} day${days === 1 ? '' : 's'} ago)`;
}
